import {
  Button,
  Field,
  FloatingTooltip,
  Textarea,
  TooltipCore,
} from '@payaca/ambrosia';
import { useSpeechRecognition } from '@payaca/hooks';
import UntitledIcon from '@payaca/untitled-icons';
import {
  type FC,
  KeyboardEventHandler,
  RefAttributes,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { useIntercom } from 'react-use-intercom';

import useGetMyRegionPreferences from '@/api/queries/me/useGetMyRegionPreferences';

import type { ChatMessage } from './types';

type Props = {
  onSend: (message: string) => void;
  disabled?: boolean;
  messages: ChatMessage[];
} & RefAttributes<HTMLTextAreaElement>;

function formatTranscript(messages: ChatMessage[]): string {
  return messages
    .filter((m) => !m.isStreaming)
    .map((m) => {
      const speaker = m.role === 'user' ? 'Me' : 'Dave';
      const text =
        m.role === 'assistant' && m.contentBlocks
          ? m.contentBlocks
              .filter((b) => b.type === 'text')
              .map((b) => b.text)
              .join('')
          : m.content;
      return `${speaker}: ${text}`;
    })
    .join('\n\n');
}

export const ChatInput: FC<Props> = (props) => {
  const { ref, onSend, disabled = false, messages } = props;
  const { show, showNewMessage } = useIntercom();
  const { data: regionPreferences } = useGetMyRegionPreferences();

  const [value, setValue] = useState('');

  const appendTranscriptToValue = useCallback((text: string) => {
    setValue((prev) => (prev ? `${prev} ${text}` : text));
  }, []);
  const {
    isListening,
    isSupported,
    interimTranscript,
    stop: stopListening,
    toggle: toggleListening,
  } = useSpeechRecognition({
    onTranscript: appendTranscriptToValue,
    lang: regionPreferences?.locale,
  });

  const displayValue =
    interimTranscript != null
      ? value
        ? `${value} ${interimTranscript}`
        : interimTranscript
      : value;

  // Stop listening when the chat becomes disabled (e.g. while Dave is replying)
  useEffect(() => {
    if (disabled && isListening) {
      stopListening();
    }
  }, [disabled, isListening, stopListening]);

  const handleKeyDown: KeyboardEventHandler<HTMLTextAreaElement> = (e) => {
    // Enter without modifier keys submits (like Slack)
    if (
      e.key === 'Enter' &&
      !e.shiftKey &&
      !e.ctrlKey &&
      !e.altKey &&
      !e.metaKey
    ) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleSubmit = () => {
    if (displayValue.trim() && !disabled) {
      stopListening();
      onSend(displayValue.trim());
      setValue('');
    }
  };

  const micLabel = isListening ? 'Stop listening' : 'Speak to Dave';

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        handleSubmit();
      }}
    >
      <Field.Legacy className="relative">
        <Textarea
          ref={ref}
          className="resize-none pb-11"
          autoHeight
          rows={1}
          maxRows={10}
          placeholder={isListening ? 'Dave is listening...' : 'Ask Dave...'}
          value={displayValue}
          onChange={setValue}
          onKeyDown={handleKeyDown}
          autoFocus
        />

        <FloatingTooltip placement="right">
          <Button
            aria-label="Speak to a human"
            className="absolute bottom-2 left-2 m-0"
            variant="ghost"
            colour="grey"
            size="xs"
            disabled={disabled}
            onClick={() => {
              const hasUserMessages = messages.some((m) => m.role === 'user');
              if (hasUserMessages) {
                showNewMessage(formatTranscript(messages));
              } else {
                show();
              }
            }}
          >
            <UntitledIcon name="help-circle.3" className="size-5" />
          </Button>

          <TooltipCore>Speak to a human</TooltipCore>
        </FloatingTooltip>

        {isSupported && (
          <FloatingTooltip placement="top">
            <Button
              aria-label={micLabel}
              aria-pressed={isListening}
              className="absolute bottom-2 right-10 m-0 mr-2"
              variant="ghost"
              colour={isListening ? 'red' : 'grey'}
              size="xs"
              disabled={disabled && !isListening}
              onClick={toggleListening}
            >
              <UntitledIcon
                name={isListening ? 'stop-square.3' : 'microphone-01.3'}
                className={
                  isListening
                    ? 'size-5 animate-pulse [animation-duration:1.5s]'
                    : 'size-5'
                }
              />
            </Button>

            <TooltipCore>{micLabel}</TooltipCore>
          </FloatingTooltip>
        )}

        <Button
          className="absolute bottom-2 right-2"
          type="submit"
          size="xs"
          disabled={disabled || !displayValue.trim()}
        >
          <UntitledIcon name="send-01.3" className="size-5" />
        </Button>
      </Field.Legacy>
      <p className="mt-1 text-center supporting-body text-gray-500">
        Dave is AI and can make mistakes. Please double check responses.
      </p>
    </form>
  );
};
